<template>
  <div class="max-w-6xl mx-auto px-4 py-10 text-gray-800 dark:text-gray-200 font-[Trebuchet_MS,sans-serif]">

    <!-- Header -->
    <div class="bg-[#9D9687] px-4 py-3 rounded-md flex items-center justify-between text-white shadow">
      <div class="flex items-center gap-3">
        <img src="/assets/logos/aim-logo-2021.png" class="h-7" alt="AIM Logo">
        <span class="italic font-semibold text-sm sm:text-base">AIM Support Portal 602.808.9552</span>
      </div>
    </div>

    <h2 class="text-3xl font-bold mt-10 mb-6 border-b border-gray-300 pb-2">IT-Network Service Level Agreement (SLA)</h2>

    <!-- Sections -->
    <SLASection title="Objectives">
      <ul class="list-disc list-inside space-y-2">
        <li>To create an environment which is conducive to a co-operative relationship between AIM and Client to ensure the effective delivery of IT services.</li>
        <li>To provide clear reference to service ownership, accountability, roles and/or responsibilities.</li>
        <li>To define in detail the services to be delivered by AIM and the level of service which can be expected by Client, thereby reducing the risk of misunderstandings.</li>
        <li>To match perceptions of expected service provision with actual service support & delivery.</li>
      </ul>
    </SLASection>

    <SLASection title="Complaints">
      <p class="mb-2">In order for us to provide the best service possible, we encourage you contact AIM management for any of the following reasons:</p>
      <ul class="list-disc list-inside space-y-2">
        <li>Response time outside the expected levels as listed.</li>
        <li>Loss of confidence or failure in providing solution or resolution.</li>
        <li>Unprofessional behavior or appearance by an AIM staff member.</li>
        <li>Any other issue relating to the relationship between AIM and Client.</li>
      </ul>
      <p class="mt-2">The intent is to ensure thorough, timely and open resolution of any problem that may come up.</p>
    </SLASection>

    <SLASection title="Submitting New Service Requests">
      <ul class="list-disc list-inside space-y-2">
        <li>
          The preferred method to insure prompt service and communication is by submitting through the request form found using AIM's support portal located at
          <a href="https://support.aimitservices.com" class="text-blue-500 underline ml-1">https://support.aimitservices.com</a>. With AIM's support portal, you can view and provide updates and additional documentation on existing requests.
        </li>
        <li>
          You can also email a detailed description of the request with supporting documentation to
          <a href="mailto:support@aimitservices.com" class="text-blue-500 underline ml-1">support@aimitservices.com</a>. This automatically enters AIM's system to provide for automated communication.
        </li>
        <li>
          You are welcome to use the 'Old-Fashioned Way' and simply call our office directly at 602.808.9552. A representative will create a support request based on the details of your conversation.
        </li>
        <li>
          Requests should be submitted individually unless directly related to one another. Example: two software titles to be installed on same PC would be acceptable in same request. One software install on a workstation and another on a server would not be acceptable in same request.
        </li>
        <li>
          Emailing any AIM staff member directly is NOT an acceptable way to submit new service requests. A request received by this method will NOT be considered received if and until a ticket is created within AIM's system.
        </li>
      </ul>
    </SLASection>

    <SLASection title="Urgent Support Requests Outside Standard Hours">
      <ul class="list-disc list-inside space-y-2">
        <li>If the network or a critical application is down, check the emergency box in new request form or use extension 9 if submitting by phone.</li>
        <li>AIM's after hours support group will be paged and you are typically contacted within 15 minutes.</li>
        <li>Instead of the common non-motivating method of having one network administrator on call for 24/7 support, the first responder from AIM's after hours support group is rewarded with a bonus. This competitive model has resulted in instances of one minute response times and an average response time during non-standard hours of less than 15 minutes.</li>
        <li>Despite our incentive program, obvious delays in contacting the proper support staff after standard business hours can occur.</li>
        <li>Additional fees for emergency service may be incurred.</li>
      </ul>
    </SLASection>

    <SLASection title="Priority of Support Requests">
      <ul class="list-disc list-inside space-y-2">
        <li>AIM accepts all requests for service or information with the intent on providing the service or resolving an issue as soon as possible.</li>
        <li>Client acknowledges that this agreement establishes a 'shared' support model and AIM provides IT services to multiple clients, receiving numerous service requests each day.</li>
        <li>Because of the potential for a high volume of requests in a short period of time, each new request is delegated and prioritized by AIM's management based on its critical nature.</li>
        <li>Five levels of impact are listed below with their corresponding response expectations and guarantees.</li>
      </ul>
    </SLASection>

    <!-- Table -->
    <div class="overflow-x-auto mt-6">
      <table class="w-full text-sm border border-gray-300 dark:border-gray-600 table-auto">
        <thead class="bg-gray-100 dark:bg-gray-700">
          <tr>
            <th class="border px-4 py-2">Issue/Request Impact</th>
            <th class="border px-4 py-2">Description/Examples</th>
            <th class="border px-4 py-2">Response Expectations</th>
            <th class="border px-4 py-2">Response Guarantee</th>
          </tr>
        </thead>
        <tbody class="bg-white dark:bg-gray-800">
          <tr>
            <td class="border px-4 py-2 text-red-600 font-bold">Extreme</td>
            <td class="border px-4 py-2">Network server or critical application down, operations halted.</td>
            <td class="border px-4 py-2">Immediate (contact within 15 minutes). Highest priority until resolved.</td>
            <td class="border px-4 py-2">Within 15 minutes during standard hours of service. Within 1 hour outside standard hours of service.</td>
          </tr>
          <tr>
            <td class="border px-4 py-2 text-orange-600 font-bold">Urgent</td>
            <td class="border px-4 py-2">Issue with high profile workstation or less than critical application, operations hampered but not halted.</td>
            <td class="border px-4 py-2">Within 1 hour</td>
            <td class="border px-4 py-2">Within 4 hours</td>
          </tr>
          <tr>
            <td class="border px-4 py-2 text-yellow-600 font-bold">High</td>
            <td class="border px-4 py-2">Workstation on network down but other workstations available or non-critical application down, operations inconvenience.</td>
            <td class="border px-4 py-2">Within 4 hours</td>
            <td class="border px-4 py-2">By 12PM of following business day</td>
          </tr>
          <tr>
            <td class="border px-4 py-2 text-blue-600 font-bold">Moderate</td>
            <td class="border px-4 py-2">New user, new/repair of PC, new software installation, etc., no or minimal impact on current operations.</td>
            <td class="border px-4 py-2">Scheduled and/or handled within 5 business days</td>
            <td class="border px-4 py-2 italic text-gray-500">NONE</td>
          </tr>
          <tr>
            <td class="border px-4 py-2 text-gray-600 font-bold">Low</td>
            <td class="border px-4 py-2">Non-critical hardware or application change, no impact on current operations.</td>
            <td class="border px-4 py-2">When convenient or scheduled within 1 month</td>
            <td class="border px-4 py-2 italic text-gray-500">NONE</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Footer -->
    <p class="text-xs text-center text-gray-500 mt-8">
      © AIM IT Services, LLC. Designed and Hosted by
      <a href="http://www.aimitservices.com" class="text-blue-500 underline">AIM IT Services</a>.
    </p>
  </div>
</template>

<script setup>
import SLASection from '@/views/components/SLASection.vue'
</script>
